import { useMutation, useQueryClient } from "@tanstack/react-query";
import useAthenaAPIClient from "../../../../hooks/queries/useAthenaAPIClient/useAthenaAPIClient";
import { useIntl } from "react-intl";
import { useContext } from "react";
import { errorStore } from "../../../../contexts/ErrorContext";
import { userKeys } from "./keys";
import { Classroom, Student as UserStudent } from "../../../../interfaces/User";
import { Dashboard } from "../../../../interfaces/Dashboard";
import queryKeys from "../../../../hooks/queries/useAthenaAPIClient/queryKeys";
import { useUserId } from "../../../../hooks/useUserInfo";
import {
    addStudentsInClassroom,
    createStudentErrorInfo,
    editStudentErrorInfo,
    renameStudentInDashboard,
    renameStudentInSession,
    updateDashboardWithStudents,
    updateSessionWithStudents,
    updateStudentInClassroom,
    userStudentToSessionStudent,
} from "./utils";
import { sessionStore } from "../../../../contexts/SessionContext";
import { useClassrooms } from "../../../../hooks/useSession";

export const useEditStudentMutation = (
    classroomId: string,
    userId: string,
    opts?: Partial<{ onSuccess: () => void }>
) => {
    const intl = useIntl();
    const { setErrorInfo } = useContext(errorStore);
    const athenaAPIClient = useAthenaAPIClient();
    const queryClient = useQueryClient();
    const { setSession } = useContext(sessionStore);
    const classroomsList = useClassrooms();

    return useMutation({
        mutationFn: (userName: { first_name: string; last_name: string }) =>
            athenaAPIClient.updateUser("student", userId, userName),
        onSuccess: (data) => {
            queryClient.setQueryData<Dashboard>(
                queryKeys.getDashboard(userId, classroomsList),
                (prevData) => {
                    console.log("getDashboard prevdata:", prevData);
                    if (!prevData) return undefined;
                    return renameStudentInDashboard(
                        prevData,
                        data as UserStudent,
                        classroomId
                    );
                }
            );

            queryClient.setQueryData<Classroom>(
                userKeys.getClassroom(classroomId),
                (prevData) => {
                    console.log("getClassroom prevdata:", prevData);
                    if (!prevData) return undefined;
                    return updateStudentInClassroom(prevData, data);
                }
            );

            setSession((session) =>
                renameStudentInSession(
                    session,
                    data as UserStudent,
                    classroomId
                )
            );
            if (opts?.onSuccess) {
                opts.onSuccess();
            }
        },
        onError: () => {
            setErrorInfo(editStudentErrorInfo(intl));
        },
    });
};

export const useCreateStudentsMutation = (
    classroomId: string,
    opts?: Partial<{ onSuccess: () => void }>
) => {
    const athenaAPIClient = useAthenaAPIClient();
    const queryClient = useQueryClient();
    const userId = useUserId();
    const { setSession } = useContext(sessionStore);
    const intl = useIntl();
    const { setErrorInfo } = useContext(errorStore);
    const classroomsList = useClassrooms();
    return useMutation({
        mutationFn: (students: UserStudent[]) =>
            athenaAPIClient.createStudents(students),
        onError: () => {
            setErrorInfo(createStudentErrorInfo(intl));
        },
        onSuccess: (data) => {
            queryClient.setQueryData<Classroom>(
                userKeys.getClassroom(classroomId),
                (prevData) => {
                    if (!prevData) return undefined;
                    return addStudentsInClassroom(prevData, data);
                }
            );
            queryClient.setQueryData<Dashboard>(
                queryKeys.getDashboard(userId, classroomsList),
                (prevData) => {
                    if (!prevData) return undefined;
                    return updateDashboardWithStudents(
                        prevData,
                        data,
                        classroomId
                    );
                }
            );
            setSession((session) =>
                updateSessionWithStudents(
                    session,
                    data.map((student) => userStudentToSessionStudent(student)),
                    classroomId
                )
            );

            if (opts?.onSuccess) {
                opts.onSuccess();
            }
        },
    });
};
